import { ILocalizationSettings, ILocalizable, TranslationType, IMessageId, ILocale } from "./localization.types";

let settings: ILocalizationSettings;

const commonDivision = "common";
const validationDivision = "validation";

let allowedDivisions = [commonDivision, validationDivision];
const messageIdRegExp = new RegExp("^([a-z]+[a-zA-Z0-9]*[A-Z]?)$");

export const isValidMessageId = (messageId: string): boolean => {
  return messageIdRegExp.test(messageId);
};

const getTranslationMessageId = (translationDivision: string, translationType: TranslationType, messageId: string): IMessageId => {
  if (!isValidMessageId(messageId)) {
    throw new Error("Incorrect message id format. `messageId` should be camelCase and can include digits");
  }

  return {
    value: `${settings.messageIdProvider.prefix}-${translationDivision}-${translationType}-${messageId}`
  };
};

export const messageIdProvider = (translationDivision: string) => {
  if (allowedDivisions.filter(e => e === translationDivision).length > 0) {
    return {
      action: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Action, messageId);
      },
      badge: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Badge, messageId);
      },
      error: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Error, messageId);
      },
      filter: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Filter, messageId);
      },
      heading: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Heading, messageId);
      },
      info: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Info, messageId);
      },
      nav: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Nav, messageId);
      },
      option: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Option, messageId);
      },
      property: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Property, messageId);
      },
      summary: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Summary, messageId);
      },
      tile: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Tile, messageId);
      },
      title: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Title, messageId);
      },
      warn: (messageId: string) => {
        return getTranslationMessageId(translationDivision, TranslationType.Warn, messageId);
      }
    };
  } else {
    throw new Error("Incorrect division");
  }
};

export const isLocalizable = (value?: ILocalizable | string): value is ILocalizable => {
  return !!value && !(typeof value === "string");
};

export const registerTranslationDivisions = (translationDivisions: string[]): string[] => {
  allowedDivisions = [...translationDivisions];

  return Array.from(allowedDivisions);
};

export const setUpLocalizationSettings = (localizationSettings: ILocalizationSettings) => {
  settings = localizationSettings;

  return localizationSettings;
};

export const commonMessages = messageIdProvider(commonDivision);

export const validationMessages = messageIdProvider(validationDivision);

export const isDefaultLocale = (locale?: string | ILocale) => {
  if (locale) {
    if (typeof locale == "string") {
      return settings.defaultLocaleIdentifiers.indexOf(locale) >= 0;
    } else {
      return settings.defaultLocaleIdentifiers.indexOf(locale.localeIdentifier) >= 0;
    }
  }

  return false;
};
